<template>
  <v-container fluid>
    <v-card class="mx-auto mt-5" outlined tile>
      <v-toolbar flat>
        <v-row>
          <v-col cols="2">
            <v-select
              hide-details
              v-model="searchBy"
              prepend-icon="manage_search"
              :items="searchByItems"
            />
          </v-col>
          <v-col cols="10">
            <v-text-field
              label="Search position / role"
              type="text"
              prepend-icon="search"
              clearable
              v-model="search"
              hide-details
              v-if="searchBy === 'positionLike'"
            />
            <registration-search-field
              placeholder="Search"
              v-model="search"
              hide-details
              prepend-icon="search"
              v-if="searchBy === 'registrationId'"
              v-bubble.error
            />
            <person-search-field
              placeholder="Search"
              v-model="search"
              hide-details
              prepend-icon="search"
              v-if="searchBy === 'personId'"
              v-bubble.error
            />
          </v-col>
        </v-row>
      </v-toolbar>

      <v-data-table
        dense
        must-sort
        item-key="_id"
        :headers="headers"
        :options.sync="options"
        :footer-props="footerProps"
        :server-items-length="list.count"
        :items="list.items"
        :loading="$apollo.queries.list.loading"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>{{ _.get(item, 'person.personId') }}</td>
            <td>
              <router-link :to="{ name: 'AdminPerson', params: { id: item.personId } }">
                {{ _.get(item, 'person.name') }}
              </router-link>
            </td>
            <td>
              <router-link :to="{ name: 'AdminRegistration', params: { id: item.registrationId } }">
                {{ _.get(item, 'registration.memberNumber') || 'Incomplete registration' }}
              </router-link>
            </td>
            <td>{{ item.position }}</td>
            <td>{{ item.start | formatDate }}</td>
            <td>{{ item.end | formatDate }}</td>
            <td>{{ item.person.lastLoggedInAt | formatDate }}</td>
            <td>{{ item.person.matchedPersons.length }}</td>
            <td class="text-end">
              <v-btn
                v-show="item.person.matchedPersons.length > 0"
                small
                icon
                @click="duplicatesDialog = true; duplicatesDialogItem = item.person"
              >
                <v-icon color="primary">merge</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="duplicatesDialog"
      max-width="1800"
    >
      <v-card min-height="90vh">
        <v-card-title>
          Detected Duplicates
        </v-card-title>

        <v-card-text>
          <person-duplicate-list
            v-if="duplicatesDialogItem"
            :personId="duplicatesDialogItem._id"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import gql from 'graphql-tag'

import gqlSortMixin from '../mixins/gql-sort-mixin'
import PersonSearchField from '../components/inputs/PersonSearchField.vue'
import RegistrationSearchField from '../components/inputs/RegistrationSearchField.vue'
import PersonDuplicateList from '../components/PersonDuplicateList.vue'

export default {
  name: 'AdminRegistrationPersonListView',

  mixins: [
    gqlSortMixin
  ],

  components: {
    PersonSearchField,
    RegistrationSearchField,
    PersonDuplicateList
  },

  data: () => ({
    search: '',
    headers: [
      {
        text: 'MSA Person ID',
        width: '10%',
        value: 'person.personId',
        sortable: false
      },
      {
        text: 'Name',
        value: 'person.name',
        width: '15%',
        sortable: false
      },
      {
        text: 'Member Number',
        value: 'registration.memberNumber',
        width: '15%',
        sortable: false
      },
      {
        text: 'Position',
        value: 'position',
        width: '10%'
      },
      {
        text: 'Start Date',
        value: 'start',
        width: '10%'
      },
      {
        text: 'End Date',
        value: 'end',
        width: '10%'
      },
      {
        text: 'Last Log In',
        value: 'person.lastLoggedInAt',
        sortable: false,
        width: '10%'
      },
      {
        text: 'Duplicates',
        value: 'matchedPersons',
        sortable: false,
        width: '10%'
      },
      {
        text: 'Actions',
        value: 'actions',
        class: 'text-end',
        width: '10%'
      }
    ],
    options: {
      multiSort: false,
      mustSort: true,
      sortDesc: [false],
      sortBy: ['position'],
      page: 1,
      itemsPerPage: 25
    },
    footerProps: {
      'items-per-page-options': [10, 25, 50, 100, 200, 500]
    },
    list: {
      count: 0,
      items: []
    },
    searchBy: 'registrationId',
    searchByItems: [
      { text: 'Member', value: 'registrationId' },
      { text: 'Person', value: 'personId' },
      { text: 'Position', value: 'positionLike' }
    ],
    duplicatesDialog: false,
    duplicatesDialogItem: null
  }),

  apollo: {
    list: {
      query: gql`query RegistrationPersonList($page: Int!, $perPage: Int!, $filter: FilterFindManyRegistrationPersonInput!, $sort: SortFindManyRegistrationPersonInput!) {
        list: registrationPersonManyPagination(page: $page, perPage: $perPage, filter: $filter, sort: $sort) {
          count
          items {
            _id
            position
            start
            end
            personId
            registrationId
            person {
              _id
              name
              personId
              lastLoggedInAt
              matchedPersons {
                _id
              }
            }
            registration {
              _id
              memberNumber
            }
          }
        }
      }`,
      debounce: 250,
      variables () {
        const filter = {
          OR: [{
            end: null
          }, {
            _operators: {
              end: {
                gt: new Date()
              }
            }
          }]
        }
        if (this.search) filter[this.searchBy] = this.search
        return {
          filter,
          sort: this.gqlSort(this.options),
          page: this.options.page,
          perPage: this.options.itemsPerPage
        }
      },
      error (e) {
        this.onError(e)
      }
    }
  },

  methods: {
    onError (e) {
      this.$emit('error', e)
    }
  },

  watch: {
    searchBy () {
      this.search = ''
    },

    search () {
      this.options.page = 1
    }
  }
}
</script>
